import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * ScrollReveal — global IntersectionObserver that adds `.is-visible` to any
 * element marked with `[data-reveal]` once it enters the viewport.
 *
 * Mount once at the app root. Re-scans on route change so dynamically rendered
 * sections (per-page) still get their reveal effect.
 */
export default function ScrollReveal() {
    const location = useLocation();

    useEffect(() => {
        if (typeof window === "undefined") return;
        const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

        const elements = document.querySelectorAll("[data-reveal]:not(.is-visible)");
        if (elements.length === 0) return;

        if (reduced) {
            elements.forEach((el) => el.classList.add("is-visible"));
            return;
        }

        const io = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add("is-visible");
                        io.unobserve(entry.target);
                    }
                });
            },
            { threshold: 0.12, rootMargin: "0px 0px -6% 0px" }
        );

        elements.forEach((el) => io.observe(el));

        // If any element is already in view on mount, reveal it a tick later so
        // the transition still plays. Reads are batched before writes to avoid
        // a per-element forced reflow (getBoundingClientRect after classList.add).
        requestAnimationFrame(() => {
            const viewportH = window.innerHeight;
            const inView = [];
            elements.forEach((el) => {
                const r = el.getBoundingClientRect();
                if (r.top < viewportH && r.bottom > 0) inView.push(el);
            });
            inView.forEach((el) => {
                el.classList.add("is-visible");
                io.unobserve(el);
            });
        });

        return () => io.disconnect();
        // re-run when path changes so newly mounted page content gets observed
    }, [location.pathname]);

    return null;
}
